/**
 * Config Store (Zustand)
 *
 * Manages app configuration and user language preference
 * - Fetches config from backend on app load
 * - Detects browser language
 * - Allows language override (persisted in localStorage)
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AppConfigResponse } from '../api/types';
import { STORAGE_KEY_CONFIG } from '@/lib/constants';

export type SupportedLanguage = 'de' | 'fr' | 'en';

interface ConfigState {
  // Config from backend. There is NO fallback config: `config` is either a
  // real, schema-valid backend response, or null. A failed fetch is recorded
  // in `configError`, never papered over with fabricated data (see
  // api/endpoints/config.ts) — consumers (App.tsx route guards) must render
  // a visible error+retry state rather than proceed on a null/stale config.
  config: AppConfigResponse | null;
  isConfigLoaded: boolean;
  /** Set by a failed GET /config (network or schema-validation failure).
   *  Mutually exclusive with isConfigLoaded — cleared by setConfig/resetConfig. */
  configError: Error | null;

  // Language (browser detection + override)
  detectedLanguage: SupportedLanguage;
  userLanguage: SupportedLanguage | null; // User's explicit choice (overrides detection)

  // Actions
  setConfig: (config: AppConfigResponse) => void;
  setConfigError: (error: Error) => void;
  setUserLanguage: (language: SupportedLanguage) => void;
  clearUserLanguage: () => void;
  // Reset config to allow retry
  resetConfig: () => void;
}

/**
 * Detect browser language and map to supported languages
 */
function detectBrowserLanguage(): SupportedLanguage {
  const browserLang = navigator.language.toLowerCase();

  if (browserLang.startsWith('de')) return 'de';
  if (browserLang.startsWith('fr')) return 'fr';
  if (browserLang.startsWith('en')) return 'en';

  // Default to German for Swiss users
  return 'de';
}

export const useConfigStore = create<ConfigState>()(
  persist(
    (set) => ({
      config: null,
      isConfigLoaded: false,
      configError: null,
      detectedLanguage: detectBrowserLanguage(),
      userLanguage: null,

      setConfig: (config) =>
        set({ config, isConfigLoaded: true, configError: null }),

      setConfigError: (error) =>
        set({ config: null, isConfigLoaded: false, configError: error }),

      setUserLanguage: (language) =>
        set({ userLanguage: language }),

      clearUserLanguage: () =>
        set({ userLanguage: null }),

      resetConfig: () =>
        set({ config: null, isConfigLoaded: false, configError: null }),
    }),
    {
      name: STORAGE_KEY_CONFIG,
      storage: createJSONStorage(() => localStorage),
      // Only persist user's explicit language choice
      partialize: (state) => ({ userLanguage: state.userLanguage }),
    }
  )
);

/**
 * Get the active language (user override or detected)
 */
export function useActiveLanguage(): SupportedLanguage {
  const { userLanguage, detectedLanguage } = useConfigStore();
  return userLanguage ?? detectedLanguage;
}
